import { useEffect, useState } from 'react';
import {
    fetchCurrentUserWithPlan,
    getMentorshipRole,
    isMentor,
    isMentee,
    isAdmin,
    type BackendUserWithPlan,
} from '../../userService';
import { Skeleton } from '../Skeleton';
import RoleOnboardingSplash from './RoleOnboardingSplash';
import MenteeMentorshipView from './MenteeMentorshipView';
import MentorReviewDashboard from './MentorReviewDashboard';
import AdminMentorshipView from './AdminMentorshipView';
import './mentorship.css';

/**
 * MentorshipView — role-gated shell rendered inside the Coaching sub-tab
 * of EducationView when the user has any mentorship role (mentor, mentee,
 * or admin). For users with role===null, the legacy course list still
 * renders by default; this component is only mounted when the parent
 * decides to swap the coaching branch.
 *
 * Owns the single getMe() fetch for the entire mentorship dashboard so
 * child views can rely on `currentUser` being present without doing
 * their own fetches.
 */

type MentorshipViewProps = {
    /** Optional pre-fetched user; if omitted, MentorshipView fetches itself. */
    initialUser?: BackendUserWithPlan | null;
};

function MentorshipView({ initialUser }: MentorshipViewProps) {
    const [user, setUser] = useState<BackendUserWithPlan | null>(initialUser ?? null);
    const [loading, setLoading] = useState(initialUser === undefined || initialUser === null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (initialUser !== undefined && initialUser !== null) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const fetched = await fetchCurrentUserWithPlan();
                if (!cancelled) {
                    setUser(fetched);
                    if (fetched === null) {
                        setError('Could not load your profile.');
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('[MentorshipView] failed to fetch user', err);
                    setError('Could not load your profile.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [initialUser]);

    if (loading) {
        return (
            <section className="mentorship-view" aria-busy="true">
                <header className="mentorship-view__header">
                    <h2 className="mentorship-view__title">Coaching</h2>
                </header>
                <div className="mentorship-view__content">
                    <Skeleton width="60%" height="20px" />
                    <Skeleton width="100%" height="120px" borderRadius="12px" />
                    <Skeleton width="100%" height="120px" borderRadius="12px" />
                </div>
            </section>
        );
    }

    if (error || !user) {
        return (
            <section className="mentorship-view">
                <header className="mentorship-view__header">
                    <h2 className="mentorship-view__title">Coaching</h2>
                </header>
                <div className="mentorship-view__content">
                    <div className="empty-state empty-error">
                        <h2>Couldn't load your profile</h2>
                        <p>{error ?? 'Please refresh and try again.'}</p>
                    </div>
                </div>
            </section>
        );
    }

    const role = getMentorshipRole(user);

    if (role === null) {
        return <RoleOnboardingSplash user={user} />;
    }

    if (isMentee(user)) {
        return <MenteeMentorshipView user={user} />;
    }

    if (isMentor(user)) {
        return <MentorReviewDashboard user={user} />;
    }

    if (isAdmin(user)) {
        return <AdminMentorshipView user={user} />;
    }

    // Unknown role — render an empty state instead of crashing
    return (
        <section className="mentorship-view">
            <header className="mentorship-view__header">
                <h2 className="mentorship-view__title">Coaching</h2>
            </header>
            <div className="mentorship-view__content">
                <div className="empty-state">
                    <h2>Unknown role</h2>
                    <p>Your profile has an unrecognized role. Contact an admin.</p>
                </div>
            </div>
        </section>
    );
}

export default MentorshipView;
